import type { Edge, Node } from '@xyflow/react';
import type { ActionNode } from './automationActionTree';

/**
 * Lays the action tree out as a real diamond-shaped flow, not just a tree:
 * a branch fans out into one column per case (+ a trailing "Else" column
 * for default_actions), and — matching automation_engine.execute_action_list's
 * actual runtime behavior, where a branch is just one action in a sequence
 * and execution continues to the next sibling after it regardless of which
 * case matched — every case's chain fans back in on whatever action follows
 * the branch in its parent list. If the branch is the last item in its
 * list, its columns stay open-ended and their bottom ids propagate upward
 * for the caller to connect.
 */

export const NODE_W = 260;
export const ROW_H = 130;
export const COL_W = 300;
const GAP_H = 40;
// 2026-09-01: the Trigger node hosts the full rule-settings form now (name/
// trigger event/description/enable/priority/stop_processing -- see
// TriggerFlowNode), not just a read-only label, so it needs real width and
// a taller reserved row than every other node's fixed ROW_H.
export const SETTINGS_NODE_W = 420;
// 2026-09-01 (live feedback: "especially the default ones they cannot be
// separated" -- the Trigger and Conditions nodes were visually overlapping,
// and neither could be dragged apart yet at the time): this estimate
// undershot the Trigger node's real rendered height (drag handle + title +
// 4 field rows, several with h-10 shadcn inputs, at 12px gaps) -- 320 was
// too tight. Bumped with real margin; both nodes are also now draggable
// (see AutomationFlowEditor.tsx's DRAGGABLE_NODE_TYPES) as a manual escape
// hatch for whatever this estimate still gets wrong -- only actually
// verifiable by looking at it in a browser.
const SETTINGS_ROW_H = 420;

export type FlowNodeData = Record<string, any>;

interface LayoutResult {
  nodes: Node[];
  edges: Edge[];
  topIds: string[];
  bottomIds: string[];
  bottomY: number;
  width: number;
}

function edge(source: string, target: string, label?: string): Edge {
  return {
    id: `e:${source}->${target}`,
    source,
    target,
    label,
    type: 'smoothstep',
    style: { strokeWidth: 1.5 },
  };
}

function caseLabel(c: any): string {
  const op = c.operator || 'equal_to';
  const opLabel: Record<string, string> = {
    equal_to: '=', not_equal_to: '≠', contains: 'contains', does_not_contain: 'not contains',
    starts_with: 'starts with', ends_with: 'ends with', greater_than: '>', less_than: '<',
    greater_than_or_equal: '≥', less_than_or_equal: '≤', is_present: 'is present', is_not_present: 'is not present',
  };
  const val = c.expected_value != null && c.expected_value !== '' ? ` ${c.expected_value}` : '';
  return `${opLabel[op] || op}${val}`;
}

export function layoutActionList(
  list: ActionNode[],
  centerX: number,
  startY: number,
): LayoutResult {
  const nodes: Node[] = [];
  const edges: Edge[] = [];

  if (!list || list.length === 0) {
    return { nodes, edges, topIds: [], bottomIds: [], bottomY: startY, width: COL_W };
  }

  let y = startY;
  let prevBottomIds: string[] = [];
  let topIds: string[] = [];
  let maxWidth = COL_W;

  for (let i = 0; i < list.length; i++) {
    const node = list[i];

    if (node.action_type !== 'branch') {
      nodes.push({
        id: node._id, type: 'action',
        position: { x: centerX - NODE_W / 2, y },
        data: { node },
        draggable: false,
      });
      if (i === 0) topIds = [node._id];
      prevBottomIds.forEach((src) => edges.push(edge(src, node._id)));
      prevBottomIds = [node._id];
      y += ROW_H;
      continue;
    }

    // Branch node
    nodes.push({
      id: node._id, type: 'branch',
      position: { x: centerX - NODE_W / 2, y },
      data: { node },
      draggable: false,
    });
    if (i === 0) topIds = [node._id];
    prevBottomIds.forEach((src) => edges.push(edge(src, node._id)));
    const branchId = node._id;
    y += ROW_H;

    const params = node.action_parameters || {};
    const cases = (params.cases || []).map((c: any) => ({
      id: c._id, isDefault: false, operator: c.operator, expected_value: c.expected_value,
      actions: c.actions || [],
    }));
    const columns = [
      ...cases,
      { id: 'default', isDefault: true, operator: undefined, expected_value: undefined, actions: params.default_actions || [] },
    ];

    const totalWidth = columns.length * COL_W;
    let colX = centerX - totalWidth / 2 + COL_W / 2;
    let colBottomY = y;
    const fanInIds: string[] = [];

    for (const col of columns) {
      const caseNodeId = col.isDefault ? `${branchId}::default` : col.id;
      nodes.push({
        id: caseNodeId, type: 'case',
        position: { x: colX - NODE_W / 2, y },
        data: {
          branchId, caseId: col.id, isDefault: col.isDefault,
          operator: col.operator, expected_value: col.expected_value,
          label: col.isDefault ? caseLabel({}) : caseLabel(col),
          field: params.field,
        },
        draggable: false,
      });
      edges.push(edge(branchId, caseNodeId, col.isDefault ? 'Else' : undefined));

      const belowY = y + ROW_H;
      if (col.actions.length === 0) {
        const ghostId = `${caseNodeId}::add`;
        nodes.push({
          id: ghostId, type: 'addAction',
          position: { x: colX - NODE_W / 2, y: belowY },
          data: { branchId, caseId: col.id },
          draggable: false,
        });
        edges.push(edge(caseNodeId, ghostId));
        colBottomY = Math.max(colBottomY, belowY + ROW_H);
      } else {
        const sub = layoutActionList(col.actions, colX, belowY);
        nodes.push(...sub.nodes);
        edges.push(...sub.edges);
        sub.topIds.forEach((id) => edges.push(edge(caseNodeId, id)));
        fanInIds.push(...sub.bottomIds);
        colBottomY = Math.max(colBottomY, sub.bottomY);
        maxWidth = Math.max(maxWidth, sub.width);
      }
      colX += COL_W;
    }

    y = colBottomY + GAP_H;
    prevBottomIds = fanInIds;
    maxWidth = Math.max(maxWidth, totalWidth);
  }

  return { nodes, edges, topIds, bottomIds: prevBottomIds, bottomY: y, width: maxWidth };
}

// A condition node's real DOM height depends on how many condition rows it
// holds — unlike every other node type here, which is a fixed ROW_H. These
// are deliberately generous estimates (extra whitespace before the action
// tree costs nothing; an underestimate would visually overlap it).
const CONDITION_HEADER_H = 90;
const CONDITION_ROW_H = 78;
const CONDITION_FOOTER_H = 56;

function conditionNodeHeight(conditionCount: number): number {
  return CONDITION_HEADER_H + Math.max(conditionCount, 1) * CONDITION_ROW_H + CONDITION_FOOTER_H;
}

/** Full graph for a rule: Trigger -> Conditions -> the action tree, with an
 * "add first action" ghost if the rule has none yet. Conditions render as
 * one real, editable node (not a read-only summary) — this is what makes
 * the flow view a complete alternative to the flat form rather than just an
 * actions-only view of it. */
export function buildRuleFlow(
  triggerEvent: string,
  conditions: any[],
  actions: ActionNode[],
): { nodes: Node[]; edges: Edge[] } {
  const centerX = 0;
  const triggerId = '__trigger__';
  const conditionId = '__conditions__';
  const nodes: Node[] = [
    // 2026-09-01: wider (SETTINGS_NODE_W) now that this node hosts the full
    // rule-settings form (TriggerFlowNode) -- `data` no longer carries
    // triggerEvent directly (settingsActions.formData.trigger_event, set in
    // AutomationFlowEditor.tsx, is what the node actually reads now).
    { id: triggerId, type: 'trigger', position: { x: centerX - SETTINGS_NODE_W / 2, y: 0 }, data: {}, draggable: false },
    { id: conditionId, type: 'condition', position: { x: centerX - NODE_W / 2, y: SETTINGS_ROW_H }, data: { conditions }, draggable: false },
  ];
  const edges: Edge[] = [edge(triggerId, conditionId)];

  const startY = SETTINGS_ROW_H + conditionNodeHeight((conditions || []).length) + GAP_H;
  if (!actions || actions.length === 0) {
    const ghostId = '__root_add__';
    nodes.push({ id: ghostId, type: 'addAction', position: { x: centerX - NODE_W / 2, y: startY }, data: { branchId: null, caseId: null }, draggable: false });
    edges.push(edge(conditionId, ghostId));
    return { nodes, edges };
  }

  const result = layoutActionList(actions, centerX, startY);
  nodes.push(...result.nodes);
  edges.push(...result.edges);
  result.topIds.forEach((id) => edges.push(edge(conditionId, id)));
  return { nodes, edges };
}
